import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  KeyRound,
  Loader2,
  LogOut,
  Download,
  RefreshCw,
  Phone,
  Mail,
  MapPin,
  Home,
  Flame,
  Users,
  Wallet,
  StickyNote,
  AlertTriangle,
  CalendarDays,
  CalendarRange,
  ChevronDown,
  Zap,
  MessageSquare,
  Trash2,
  Sparkles,
} from "lucide-react";
import AdsGenerator from "@/components/admin/AdsGenerator";
import LeadAiDrawer, { scoreCls } from "@/components/admin/LeadAiDrawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "es_admin_token";

const COLUMNS = [
  ["Date", "created_at"],
  ["Produit", "product"],
  ["Nom", "contact_name"],
  ["Téléphone", "phone"],
  ["Email", "email"],
  ["CP", "postal_code"],
  ["Logement", "property_type"],
  ["Ancienneté", "construction_period"],
  ["Surface", "surface_range"],
  ["Chauffage actuel", "heating_current"],
  ["Émetteurs", "emitters"],
  ["Pièces (clim)", "rooms"],
  ["Usage (clim)", "clim_usage"],
  ["Accroche", "variant"],
  ["Rappel 1h", "urgent"],
  ["Campagne pub", "utm_campaign"],
  ["UTM source", "utm_source", true],
  ["UTM medium", "utm_medium", true],
  ["UTM contenu", "utm_content", true],
  ["UTM terme", "utm_term", true],
  ["Foyer", "household_size"],
  ["RFR", "rfr"],
  ["Revenus", "income_bracket"],
  ["Aides / solution", "estimated_aids"],
  ["Statut", "status"],
  ["Note", "note"],
  ["", "actions", true],
];

const PRODUCTS = {
  pac: { label: "PAC air/eau", cls: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" },
  clim: { label: "Clim Daikin", cls: "border-sky-400/30 bg-sky-400/10 text-sky-300" },
};
const productOf = (l) => (l.product === "clim" ? "clim" : "pac");

const VARIANT_LABELS = {
  default: "Par défaut",
  chaleur: "Canicule / 21 °C",
  silence: "Silence / facture",
  eco: "3× moins cher",
};
const variantOf = (l) => l.variant || "default";

const waNumber = (phone) => {
  let d = (phone || "").replace(/\D/g, "");
  if (d.startsWith("0033")) d = d.slice(2);
  if (d.startsWith("0") && d.length === 10) d = "33" + d.slice(1);
  return d;
};

const waLink = (l) => {
  const first = (l.contact_name || "").trim().split(" ")[0];
  const produit = productOf(l) === "clim" ? "de climatisation Daikin" : "de pompe à chaleur air/eau";
  const msg = `Bonjour ${first}, ici Ener Solutions. Nous avons bien reçu votre demande ${produit}. Quand puis-je vous appeler pour en parler ?`;
  return `https://wa.me/${waNumber(l.phone)}?text=${encodeURIComponent(msg)}`;
};

const WhatsAppIcon = ({ className = "h-3.5 w-3.5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.47s1.06 2.87 1.21 3.07c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.88-9.89 9.88m8.41-18.3A11.8 11.8 0 0 0 12.05 0C5.5 0 .16 5.33.16 11.89c0 2.1.55 4.14 1.59 5.94L.06 24l6.3-1.65a11.9 11.9 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.33 11.89-11.89 0-3.18-1.24-6.16-3.48-8.41" />
  </svg>
);

const STATUS_CONFIG = {
  "nouveau": {
    label: "Nouveau",
    dot: "bg-amber-400",
    pill: "border-amber-400/30 text-amber-300",
    chip: "border-amber-400/30 text-amber-300",
  },
  "rappelé": {
    label: "Rappelé",
    dot: "bg-sky-400",
    pill: "border-sky-400/30 text-sky-300",
    chip: "border-sky-400/30 text-sky-300",
  },
  "devis signé": {
    label: "Devis signé",
    dot: "bg-emerald-400",
    pill: "border-emerald-400/30 text-emerald-300",
    chip: "border-emerald-400/30 text-emerald-300",
  },
};

const STATUSES = ["nouveau", "rappelé", "devis signé"];

const isToday = (iso) => {
  try {
    return new Date(iso).toDateString() === new Date().toDateString();
  } catch (e) {
    return false;
  }
};

const isThisWeek = (iso) => {
  try {
    const d = new Date(iso);
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    start.setHours(0, 0, 0, 0);
    return d >= start;
  } catch (e) {
    return false;
  }
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div
    data-testid={testid}
    className="flex items-center gap-4 rounded-2xl border border-white/10 bg-forest-surface/60 px-5 py-4"
  >
    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-400/10 text-emerald-300">
      <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
    </span>
    <div>
      <p className="font-display text-xl font-extrabold leading-none">{value}</p>
      <p className="mt-1 text-[11px] text-white/45">{label}</p>
    </div>
  </div>
);

const StatusSelect = ({ lead, index, onUpdate }) => {
  const value = lead.status || "nouveau";
  const cfg = STATUS_CONFIG[value] || STATUS_CONFIG["nouveau"];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${cfg.pill}`}
    >
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${cfg.dot}`} />
      <select
        data-testid={`admin-status-select-${index}`}
        value={value}
        onChange={(e) => onUpdate(e.target.value)}
        className="cursor-pointer appearance-none bg-transparent pr-1 text-xs font-semibold outline-none"
      >
        {STATUSES.map((s) => (
          <option key={s} value={s} className="bg-forest-dark text-white">
            {STATUS_CONFIG[s].label}
          </option>
        ))}
      </select>
    </span>
  );
};

const NoteInput = ({ lead, index, onSave }) => {
  const [value, setValue] = useState(lead.note || "");
  useEffect(() => {
    setValue(lead.note || "");
  }, [lead.id, lead.note]);

  const save = () => {
    if (value.trim() !== (lead.note || "")) onSave(value.trim());
  };

  return (
    <div className="flex items-center gap-1.5">
      <StickyNote
        className={`h-3.5 w-3.5 shrink-0 ${
          lead.note ? "text-emerald-300" : "text-white/25"
        }`}
      />
      <input
        data-testid={`admin-note-input-${index}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.target.blur();
        }}
        placeholder="Ajouter une note…"
        maxLength={500}
        className="w-44 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs text-white placeholder-white/30 outline-none transition-colors focus:border-emerald-400"
      />
    </div>
  );
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return iso || "—";
  }
};

function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [heatingFilter, setHeatingFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [smsStatus, setSmsStatus] = useState(null);
  const [smsTesting, setSmsTesting] = useState(false);

  const fetchSmsStatus = async (t) => {
    try {
      const { data } = await axios.get(`${API}/admin/sms/status`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setSmsStatus(data);
    } catch (e) {
      setSmsStatus(null);
    }
  };

  const testSms = async () => {
    setSmsTesting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/sms/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`SMS de test envoyé à ${data.results.map((r) => r.to).join(", ")}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Envoi du SMS de test impossible.");
    } finally {
      setSmsTesting(false);
    }
  };
  const fetchLeads = async (t) => {
    setLeads(null);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setLeads(data.leads || []);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setError("Session expirée, reconnectez-vous.");
      } else {
        setError("Impossible de charger les leads.");
        setLeads([]);
      }
    }
  };

  useEffect(() => {
    if (token) {
      fetchLeads(token);
      fetchSmsStatus(token);
    }
  }, [token]);

  const stats = useMemo(() => {
    if (!leads)
      return null;
    const heat = (l) => (l.heating_current || "").toLowerCase();
    return {
      total: leads.length,
      today: leads.filter((l) => isToday(l.created_at)).length,
      week: leads.filter((l) => isThisWeek(l.created_at)).length,
      fioul: leads.filter((l) => heat(l).includes("fioul")).length,
      gaz: leads.filter((l) => heat(l).includes("gaz")).length,
      pac: leads.filter((l) => productOf(l) === "pac").length,
      clim: leads.filter((l) => productOf(l) === "clim").length,
      urgent: leads.filter((l) => l.urgent).length,
    };
  }, [leads]);

  const variantStats = useMemo(() => {
    const clim = (leads || []).filter((l) => productOf(l) === "clim");
    const counts = {};
    clim.forEach((l) => {
      const v = variantOf(l);
      counts[v] = counts[v] || { total: 0, signed: 0 };
      counts[v].total += 1;
      if (l.status === "devis signé") counts[v].signed += 1;
    });
    return Object.entries(counts)
      .map(([key, c]) => ({ key, ...c, share: clim.length ? Math.round((c.total / clim.length) * 100) : 0 }))
      .sort((a, b) => b.total - a.total);
  }, [leads]);

  const campaignStats = useMemo(() => {
    const counts = {};
    (leads || []).forEach((l) => {
      if (!l.utm_source && !l.utm_campaign) return;
      const key = `${l.utm_source || "?"}|${l.utm_campaign || "—"}`;
      counts[key] = counts[key] || { source: l.utm_source || "?", campaign: l.utm_campaign || "—", total: 0, signed: 0, contents: {} };
      counts[key].total += 1;
      if (l.status === "devis signé") counts[key].signed += 1;
      if (l.utm_content) counts[key].contents[l.utm_content] = (counts[key].contents[l.utm_content] || 0) + 1;
    });
    return Object.values(counts).sort((a, b) => b.total - a.total);
  }, [leads]);

  const heatingOptions = useMemo(
    () =>
      leads
        ? Array.from(new Set(leads.map((l) => l.heating_current).filter(Boolean)))
        : [],
    [leads]
  );

  const filtered = useMemo(
    () =>
      (leads || []).filter(
        (l) =>
          (!statusFilter || (l.status || "nouveau") === statusFilter) &&
          (heatingFilter === "all" || l.heating_current === heatingFilter) &&
          (productFilter === "all" || productOf(l) === productFilter)
      ),
    [leads, statusFilter, heatingFilter, productFilter]
  );

  const resetFilters = () => {
    setStatusFilter(null);
    setHeatingFilter("all");
    setProductFilter("all");
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Mot de passe incorrect. Réessayez."
      );
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setLeads(null);
    setPassword("");
    setStatusFilter(null);
    setHeatingFilter("all");
    setProductFilter("all");
  };

  const updateStatus = async (leadId, newStatus) => {
    const prev = leads;
    setLeads((ls) =>
      ls.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l))
    );
    try {
      await axios.patch(
        `${API}/admin/leads/${leadId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Statut mis à jour : ${STATUS_CONFIG[newStatus].label}`);
    } catch (e) {
      setLeads(prev);
      toast.error("Impossible de changer le statut.");
    }
  };

  const [deletingId, setDeletingId] = useState(null);
  const [aiLeadId, setAiLeadId] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const aiLead = (leads || []).find((l) => l.id === aiLeadId) || null;

  const analyzeLead = async (lead, force = false) => {
    setAiLeadId(lead.id);
    if (lead.ai && !force) return;
    setAiBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/leads/${lead.id}/analyze`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, ai: data.ai } : l)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analyse IA impossible.");
    } finally {
      setAiBusy(false);
    }
  };

  const deleteLead = (lead) => {
    toast(`Supprimer ${lead.contact_name} ?`, {
      description: "Cette action est définitive.",
      action: {
        label: "Supprimer",
        onClick: async () => {
          setDeletingId(lead.id);
          try {
            await axios.delete(`${API}/admin/leads/${lead.id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setLeads((ls) => ls.filter((l) => l.id !== lead.id));
            toast.success("Prospect supprimé");
          } catch (e) {
            toast.error("Suppression impossible.");
          } finally {
            setDeletingId(null);
          }
        },
      },
      cancel: { label: "Annuler", onClick: () => {} },
      duration: 8000,
    });
  };

  const updateNote = async (leadId, newNote) => {
    try {
      await axios.patch(
        `${API}/admin/leads/${leadId}`,
        { note: newNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeads((ls) =>
        ls.map((l) => (l.id === leadId ? { ...l, note: newNote } : l))
      );
      toast.success("Note enregistrée");
    } catch (e) {
      toast.error("Impossible d'enregistrer la note.");
    }
  };

  const exportCsv = () => {
    const header = COLUMNS.filter(([, key]) => key !== "actions").map((c) => c[0]).join(";");
    const rows = filtered.map((l) =>
      COLUMNS.filter(([, key]) => key !== "actions").map(([, key]) =>
        String(
          key === "product"
            ? PRODUCTS[productOf(l)].label
            : key === "urgent"
              ? (l.urgent ? "oui" : "non")
              : key === "variant"
                ? (productOf(l) === "clim" ? variantOf(l) : "")
                : (l[key] ?? (key === "status" ? "nouveau" : "")) || ""
        )
          .replace(/;/g, ",")
          .replace(/\n/g, " ")
      ).join(";")
    );
    const csv = "\uFEFF" + [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-ener-solutions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-forest-deep px-5 py-24 text-white">
        <motion.form
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md rounded-3xl border border-white/10 bg-forest-surface/80 p-8 backdrop-blur-xl sm:p-10"
        >
          <span className="grid h-12 w-12 place-items-center rounded-xl bg-emerald-400/10 text-emerald-300">
            <KeyRound className="h-5 w-5" />
          </span>
          <h1 className="mt-6 font-display text-2xl font-bold">Espace leads</h1>
          <p className="mt-2 text-sm text-white/50">
            Accès réservé à l'équipe Ener Solutions.
          </p>
          <input
            data-testid="admin-input-password"
            type="password"
            placeholder="Mot de passe"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-6 w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white placeholder-white/40 outline-none transition-colors focus:border-emerald-400"
          />
          {error && (
            <p data-testid="admin-error" className="mt-3 text-sm text-red-400">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading || !password}
            className={`mt-6 flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold transition-all ${
              loading || !password
                ? "cursor-not-allowed bg-white/10 text-white/30"
                : "bg-emerald-400 text-forest-deep hover:scale-[1.02] hover:bg-emerald-300"
            }`}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Se connecter
          </button>
        </motion.form>
      </div>
    );
  }

  const hasFilters = statusFilter !== null || heatingFilter !== "all" || productFilter !== "all";

  return (
    <div className="min-h-screen bg-forest-deep px-5 py-24 text-white sm:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold sm:text-3xl">
              Espace leads
            </h1>
            <p className="mt-1 text-sm text-white/50" data-testid="admin-leads-count">
              {leads === null
                ? "Chargement…"
                : hasFilters
                  ? `${filtered.length} prospect${filtered.length > 1 ? "s" : ""} affiché${filtered.length > 1 ? "s" : ""} sur ${leads.length}`
                  : `${leads.length} prospect${leads.length > 1 ? "s" : ""} reçu${leads.length > 1 ? "s" : ""}`}
            </p>
            {leads && leads.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-white/35">
                  Filtres :
                </span>
                {Object.keys(PRODUCTS).map((p) => {
                  const active = productFilter === p;
                  return (
                    <button
                      key={p}
                      data-testid={`admin-filter-product-${p}`}
                      onClick={() => setProductFilter(active ? "all" : p)}
                      className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-all ${
                        active
                          ? PRODUCTS[p].cls + " ring-1 ring-white/40"
                          : "border-white/10 text-white/40 hover:border-white/30 hover:text-white/70"
                      }`}
                    >
                      {leads.filter((l) => productOf(l) === p).length} {PRODUCTS[p].label}
                    </button>
                  );
                })}
                <span className="h-3 w-px bg-white/15" />
                {STATUSES.map((s) => {
                  const active = statusFilter === s;
                  return (
                    <button
                      key={s}
                      data-testid={`admin-filter-status-${s.replace(/\s+/g, "-")}`}
                      onClick={() => setStatusFilter(active ? null : s)}
                      className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-all ${
                        active
                          ? STATUS_CONFIG[s].chip + " ring-1 ring-white/40"
                          : "border-white/10 text-white/40 hover:border-white/30 hover:text-white/70"
                      }`}
                    >
                      {leads.filter((l) => (l.status || "nouveau") === s).length}{" "}
                      {STATUS_CONFIG[s].label}
                    </button>
                  );
                })}
                <div className="relative">
                  <select
                    data-testid="admin-filter-heating"
                    value={heatingFilter}
                    onChange={(e) => setHeatingFilter(e.target.value)}
                    className={`appearance-none rounded-full border bg-white/5 py-0.5 pl-2.5 pr-7 text-[11px] font-semibold outline-none transition-colors ${
                      heatingFilter !== "all"
                        ? "border-emerald-400/50 text-emerald-300"
                        : "border-white/10 text-white/40 hover:border-white/30"
                    }`}
                  >
                    <option value="all" className="bg-forest-dark text-white">
                      Tous les chauffages
                    </option>
                    {heatingOptions.map((h) => (
                      <option key={h} value={h} className="bg-forest-dark text-white">
                        {h}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 text-white/40" />
                </div>
                {hasFilters && (
                  <button
                    data-testid="admin-filters-reset"
                    onClick={resetFilters}
                    className="text-[11px] font-semibold text-emerald-300 underline underline-offset-2 hover:text-emerald-200"
                  >
                    Réinitialiser
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              data-testid="admin-sms-test"
              onClick={testSms}
              disabled={smsTesting || !smsStatus?.configured}
              title={
                smsStatus?.configured
                  ? `Alertes SMS « ${smsStatus.sender} » → ${smsStatus.to.join(", ")}`
                  : "Alertes SMS non configurées"
              }
              className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition-all ${
                smsStatus?.configured
                  ? "border-sky-400/40 text-sky-300 hover:border-sky-300 hover:text-sky-200"
                  : "cursor-not-allowed border-white/15 text-white/30"
              }`}
            >
              {smsTesting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <MessageSquare className="h-4 w-4" />
              )}
              Tester le SMS
              {smsStatus?.credits != null && (
                <span
                  data-testid="admin-sms-credits"
                  className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                    smsStatus.credits < 50
                      ? "bg-red-500/20 text-red-300"
                      : "bg-sky-400/15 text-sky-200"
                  }`}
                >
                  {smsStatus.credits} SMS
                </span>
              )}
            </button>
            <button
              data-testid="admin-refresh"
              onClick={() => fetchLeads(token)}
              className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white/75 transition-all hover:border-white/50 hover:text-white"
            >
              <RefreshCw className="h-4 w-4" /> Actualiser
            </button>
            <button
              data-testid="admin-export-csv"
              onClick={exportCsv}
              disabled={!filtered || filtered.length === 0}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-all ${
                filtered && filtered.length > 0
                  ? "bg-emerald-400 text-forest-deep hover:scale-[1.03] hover:bg-emerald-300"
                  : "cursor-not-allowed bg-white/10 text-white/30"
              }`}
            >
              <Download className="h-4 w-4" /> Exporter en CSV
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="grid h-10 w-10 place-items-center rounded-full border border-white/20 text-white/60 transition-all hover:border-red-400/60 hover:text-red-400"
              aria-label="Déconnexion"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>

        {smsStatus?.credits != null && smsStatus.credits < 50 && (
          <div
            data-testid="admin-sms-credits-warning"
            className="mt-6 flex items-center gap-3 rounded-2xl border border-red-400/40 bg-red-500/10 px-5 py-3 text-sm text-red-200"
          >
            <AlertTriangle className="h-4 w-4 shrink-0 text-red-300" />
            <span>
              <strong>Crédits SMS Brevo bientôt épuisés :</strong> {smsStatus.credits} SMS restant
              {smsStatus.credits > 1 ? "s" : ""}. Rechargez vos crédits sur app.brevo.com pour
              continuer à recevoir les alertes.
            </span>
          </div>
        )}

        {stats && stats.total > 0 && (
          <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              testid="admin-stat-total"
              icon={Users}
              label="Prospects au total"
              value={stats.total}
            />
            <StatCard
              testid="admin-stat-today"
              icon={CalendarDays}
              label="Aujourd'hui"
              value={stats.today}
            />
            <StatCard
              testid="admin-stat-week"
              icon={CalendarRange}
              label="Cette semaine"
              value={stats.week}
            />
            <StatCard
              testid="admin-stat-fioulgaz"
              icon={Flame}
              label="PAC · Clim"
              value={`${stats.pac} · ${stats.clim}`}
            />
          </div>
        )}

        {variantStats.length > 0 && (
          <div
            data-testid="admin-variant-stats"
            className="mt-4 rounded-2xl border border-white/10 bg-forest-surface/60 px-5 py-4"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-300">
                Accroches Meta — page climatisation
              </p>
              <p className="text-[11px] text-white/40">
                {stats.urgent > 0 && (
                  <span className="mr-3 inline-flex items-center gap-1 text-amber-300">
                    <Zap className="h-3 w-3" /> {stats.urgent} rappel{stats.urgent > 1 ? "s" : ""} dans l'heure
                  </span>
                )}
                {stats.clim} lead{stats.clim > 1 ? "s" : ""} clim
              </p>
            </div>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {variantStats.map((v) => (
                <div
                  key={v.key}
                  data-testid={`admin-variant-${v.key}`}
                  className="rounded-xl border border-white/10 bg-white/5 px-4 py-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-white/80">
                      {VARIANT_LABELS[v.key] || v.key}
                    </p>
                    <span className="font-mono text-[10px] text-white/40">?v={v.key}</span>
                  </div>
                  <p className="mt-1 font-display text-xl font-extrabold leading-none">
                    {v.total}
                    <span className="ml-1.5 text-xs font-semibold text-white/40">{v.share} %</span>
                  </p>
                  <div className="mt-2 h-1 overflow-hidden rounded-full bg-white/10">
                    <div className="h-full rounded-full bg-sky-400" style={{ width: `${v.share}%` }} />
                  </div>
                  <p className="mt-1.5 text-[10px] text-white/40">
                    {v.signed} devis signé{v.signed > 1 ? "s" : ""}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <AdsGenerator token={token} />

        {campaignStats.length > 0 && (
          <div
            data-testid="admin-campaign-stats"
            className="mt-4 rounded-2xl border border-white/10 bg-forest-surface/60 px-5 py-4"
          >
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-300">
              Campagnes publicitaires (UTM)
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {campaignStats.map((c) => (
                <div
                  key={`${c.source}|${c.campaign}`}
                  data-testid={`admin-campaign-${c.source}-${c.campaign}`.replace(/\s+/g, "-")}
                  className="rounded-xl border border-white/10 bg-white/5 px-4 py-3"
                >
                  <p className="truncate text-xs font-semibold text-white/80">{c.campaign}</p>
                  <p className="font-mono text-[10px] text-white/40">{c.source}</p>
                  <p className="mt-1 font-display text-xl font-extrabold leading-none">
                    {c.total}
                    <span className="ml-1.5 text-xs font-semibold text-emerald-300">
                      {c.signed} signé{c.signed > 1 ? "s" : ""}
                    </span>
                  </p>
                  {Object.keys(c.contents).length > 0 && (
                    <p className="mt-1.5 truncate text-[10px] text-white/40">
                      {Object.entries(c.contents)
                        .sort((a, b) => b[1] - a[1])
                        .map(([k, v]) => `${k} (${v})`)
                        .join(" · ")}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {leads === null ? (
          <div className="mt-16 flex items-center justify-center text-white/50">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <div
            data-testid="admin-empty"
            className="mt-16 rounded-3xl border border-dashed border-white/15 p-16 text-center"
          >
            <Home className="mx-auto h-8 w-8 text-white/30" />
            <p className="mt-4 font-display text-lg font-bold">
              Aucun prospect pour le moment
            </p>
            <p className="mt-2 text-sm text-white/45">
              Les demandes envoyées via le simulateur apparaîtront ici
              automatiquement.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="admin-filter-empty"
            className="mt-10 rounded-3xl border border-dashed border-white/15 p-16 text-center"
          >
            <p className="font-display text-lg font-bold">
              Aucun prospect ne correspond à ces filtres
            </p>
            <button
              onClick={resetFilters}
              className="mt-4 rounded-full bg-emerald-400 px-5 py-2.5 text-sm font-bold text-forest-deep transition-all hover:bg-emerald-300"
            >
              Réinitialiser les filtres
            </button>
          </div>
        ) : (
          <div
            data-testid="admin-leads-table"
            className="mt-10 overflow-x-auto rounded-3xl border border-white/10"
          >
            <table className="w-full min-w-[2000px] text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-white/5 font-mono text-[11px] uppercase tracking-wider text-emerald-300">
                  {COLUMNS.filter(([, key, csvOnly]) => !csvOnly || key === "actions").map(([label, key]) => (
                    <th key={key} className="px-4 py-4 font-semibold">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((l, i) => (
                  <tr
                    key={l.id || i}
                    data-testid={`admin-lead-row-${i}`}
                    className="border-b border-white/5 text-white/75 transition-colors hover:bg-white/5"
                  >
                    <td className="whitespace-nowrap px-4 py-4 text-white/50">
                      {fmtDate(l.created_at)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4">
                      <span
                        data-testid={`admin-lead-product-${i}`}
                        className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${PRODUCTS[productOf(l)].cls}`}
                      >
                        {PRODUCTS[productOf(l)].label}
                      </span>
                    </td>
                    <td className="px-4 py-4 font-semibold text-white">
                      {l.contact_name}
                      {l.ai?.score && (
                        <span
                          data-testid={`admin-lead-score-${i}`}
                          className={`mt-1 mr-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${scoreCls(l.ai.score)}`}
                        >
                          {l.ai.score}
                        </span>
                      )}
                      {l.urgent && (
                        <span
                          data-testid={`admin-lead-urgent-${i}`}
                          className="mt-1 flex w-fit items-center gap-1 whitespace-nowrap rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold text-amber-300"
                        >
                          <Zap className="h-3 w-3" /> Rappel dans l'heure
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4">
                      <a
                        href={`tel:${l.phone}`}
                        className="flex items-center gap-1.5 text-emerald-300 hover:underline"
                      >
                        <Phone className="h-3.5 w-3.5" /> {l.phone}
                      </a>
                      {waNumber(l.phone).length >= 11 && (
                        <a
                          href={waLink(l)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`admin-lead-whatsapp-${i}`}
                          title="Ouvrir dans WhatsApp"
                          className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-[#25D366]/40 bg-[#25D366]/10 px-2.5 py-1 text-[11px] font-semibold text-[#5EE29A] transition-all hover:bg-[#25D366]/20 hover:text-white"
                        >
                          <WhatsAppIcon /> Ouvrir dans WhatsApp
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {l.email ? (
                        <a
                          href={`mailto:${l.email}`}
                          className="flex items-center gap-1.5 hover:underline"
                        >
                          <Mail className="h-3.5 w-3.5" /> {l.email}
                        </a>
                      ) : (
                        <span className="text-white/30">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className="flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-white/40" />{" "}
                        {l.postal_code}
                      </span>
                    </td>
                    <td className="px-4 py-4">{l.property_type}</td>
                    <td className="px-4 py-4 text-white/50">
                      {l.construction_period}
                    </td>
                    <td className="px-4 py-4 text-white/50">
                      {l.surface_range}
                    </td>
                    <td className="px-4 py-4">
                      {l.heating_current ? (
                        <span className="flex items-center gap-1.5">
                          <Flame className="h-3.5 w-3.5 text-amber-400/80" />{" "}
                          {l.heating_current}
                        </span>
                      ) : (
                        <span className="text-white/30">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-white/50">{l.emitters || "—"}</td>
                    <td className="whitespace-nowrap px-4 py-4 text-white/70">{l.rooms || "—"}</td>
                    <td className="px-4 py-4 text-white/50">{l.clim_usage || "—"}</td>
                    <td className="whitespace-nowrap px-4 py-4 text-white/50">
                      {productOf(l) === "clim" ? (
                        <span className="rounded-md border border-sky-400/20 bg-sky-400/5 px-1.5 py-0.5 font-mono text-[10px] text-sky-300">
                          {variantOf(l)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-4 text-white/50">{l.urgent ? "Oui" : "—"}</td>
                    <td className="px-4 py-4">
                      {l.utm_source || l.utm_campaign ? (
                        <span
                          data-testid={`admin-lead-utm-${i}`}
                          className="flex flex-col gap-0.5 text-xs"
                        >
                          <span className="font-semibold text-white/80">
                            {l.utm_campaign || "—"}
                          </span>
                          <span className="font-mono text-[10px] text-white/40">
                            {[l.utm_source, l.utm_medium].filter(Boolean).join(" / ")}
                            {l.utm_content ? ` · ${l.utm_content}` : ""}
                          </span>
                        </span>
                      ) : (
                        <span className="text-white/30">Direct</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className="flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5 text-white/40" />{" "}
                        {l.household_size}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-white/70">
                      {l.rfr ? `${Number(l.rfr).toLocaleString("fr-FR")} €` : "—"}
                    </td>
                    <td className="px-4 py-4">{l.income_bracket}</td>
                    <td className="whitespace-nowrap px-4 py-4 font-semibold text-emerald-300">
                      <span className="flex items-center gap-1.5">
                        <Wallet className="h-3.5 w-3.5" /> {l.estimated_aids}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <StatusSelect
                        lead={l}
                        index={i}
                        onUpdate={(s) => updateStatus(l.id, s)}
                      />
                    </td>
                    <td className="px-4 py-4">
                      <NoteInput
                        lead={l}
                        index={i}
                        onSave={(n) => updateNote(l.id, n)}
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1.5">
                      <button
                        data-testid={`admin-ai-lead-${i}`}
                        onClick={() => analyzeLead(l)}
                        title="Analyse IA (résumé, score, brouillon de message)"
                        className={`grid h-8 w-8 place-items-center rounded-lg border transition-all ${
                          l.ai ? scoreCls(l.ai.score) : "border-violet-400/30 text-violet-300 hover:bg-violet-400/10"
                        }`}
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                      </button>
                      <button
                        data-testid={`admin-delete-lead-${i}`}
                        onClick={() => deleteLead(l)}
                        disabled={deletingId === l.id}
                        title="Supprimer ce prospect"
                        className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 text-white/40 transition-all hover:border-red-400/60 hover:bg-red-400/10 hover:text-red-300"
                      >
                        {deletingId === l.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {leads && leads.length > 0 && (
          <p className="mt-6 flex items-center gap-2 text-xs text-white/35">
            <AlertTriangle className="h-3.5 w-3.5" /> Données personnelles —
            usage interne Ener Solutions uniquement.
          </p>
        )}
      </div>
      <LeadAiDrawer
        lead={aiLead}
        busy={aiBusy}
        onClose={() => setAiLeadId(null)}
        onRefresh={() => aiLead && analyzeLead(aiLead, true)}
      />
    </div>
  );
}

export default Admin;
